// components/dispense/SaleVatBreakdown.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Product-level VAT breakdown for a single sale.
//
// Reads the snapshot taken at checkout (/dispense/sales/:id/vat-breakdown), so
// the figures shown are exactly what was charged at the time — later changes to
// VAT rates or category apportionment never alter a historic sale.

import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";

import { ISaleVatBreakdown } from "../../@types/dispense";
import { DispenseService } from "../../api/dispense.service";
import { fCurrency } from "../../utils/formatNumber";

const dispenseService = new DispenseService();

type Props = {
  dispenseId: number;
  /** Hide the outer Card when embedding inside an existing panel. */
  bare?: boolean;
};

export default function SaleVatBreakdown({ dispenseId, bare = false }: Props) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ISaleVatBreakdown | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await dispenseService.getSaleVatBreakdown(dispenseId);
      setData(res.data);
    } catch (e) {
      setError("Could not load the VAT breakdown for this sale.");
    } finally {
      setLoading(false);
    }
  }, [dispenseId]);

  useEffect(() => {
    load();
  }, [load]);

  const body = (
    <>
      {loading && (
        <Stack spacing={1}>
          <Skeleton variant="text" height={32} />
          <Skeleton variant="text" height={32} />
          <Skeleton variant="text" height={32} />
        </Stack>
      )}

      {!loading && error && <Alert severity="error">{error}</Alert>}

      {!loading && !error && data && (
        <>
          {data.lines.length === 0 && !data.addonsLine && (
            <Alert severity="info">
              No VAT breakdown was recorded for this sale — it predates
              product-level VAT, or VAT calculation is switched off.
            </Alert>
          )}

          {(data.lines.length > 0 || data.addonsLine) && (
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Unit</TableCell>
                    <TableCell align="right">Gross</TableCell>
                    <TableCell align="right">Net</TableCell>
                    <TableCell align="right">VAT</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Exempt professional dispensing element (HMRC §701/57)">
                        <span>Exempt</span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.lines.map((line) => (
                    <TableRow key={line.lineId} hover>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2">{line.productName}</Typography>
                          {line.vatCategory && (
                            <Chip
                              size="small"
                              variant="outlined"
                              label={line.vatCategory.replace(/_/g, " ")}
                            />
                          )}
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          {line.sku ? `${line.sku} · ` : ""}
                          {line.vatRateApplied}% VAT
                          {line.exemptRateApplied > 0
                            ? ` · ${(line.exemptRateApplied * 100).toFixed(1)}% exempt`
                            : ""}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{line.quantity}</TableCell>
                      <TableCell align="right">{fCurrency(line.unitPrice)}</TableCell>
                      <TableCell align="right">{fCurrency(line.lineGross)}</TableCell>
                      <TableCell align="right">{fCurrency(line.lineNet)}</TableCell>
                      <TableCell align="right">{fCurrency(line.lineVat)}</TableCell>
                      <TableCell align="right">{fCurrency(line.lineExempt)}</TableCell>
                    </TableRow>
                  ))}

                  {data.addonsLine && (
                    <TableRow hover>
                      <TableCell>
                        <Typography variant="body2">{data.addonsLine.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Standard-rated
                        </Typography>
                      </TableCell>
                      <TableCell align="right">—</TableCell>
                      <TableCell align="right">—</TableCell>
                      <TableCell align="right">
                        {fCurrency(data.addonsLine.lineGross)}
                      </TableCell>
                      <TableCell align="right">
                        {fCurrency(data.addonsLine.lineNet)}
                      </TableCell>
                      <TableCell align="right">
                        {fCurrency(data.addonsLine.lineVat)}
                      </TableCell>
                      <TableCell align="right">
                        {fCurrency(data.addonsLine.lineExempt)}
                      </TableCell>
                    </TableRow>
                  )}

                  <TableRow>
                    <TableCell colSpan={3}>
                      <Typography variant="subtitle2">Total charged</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="subtitle2">
                        {fCurrency(data.totals.charged)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="subtitle2">
                        {fCurrency(data.totals.netAmount)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="subtitle2">
                        {fCurrency(data.totals.vatAmount)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="subtitle2">
                        {fCurrency(data.totals.exemptAmount)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Divider sx={{ my: 2 }} />

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={3}
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="caption" color="text.secondary">
                Paid in app
              </Typography>
              <Typography variant="subtitle1">
                {fCurrency(data.payments.totalPaid)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Refunded
              </Typography>
              <Typography variant="subtitle1">
                {fCurrency(data.payments.totalRefunded)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Outstanding
              </Typography>
              <Typography
                variant="subtitle1"
                color={data.payments.outstanding > 0 ? "warning.main" : "success.main"}
              >
                {fCurrency(data.payments.outstanding)}
              </Typography>
            </Box>
          </Stack>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: "block" }}>
            Gross = Net + VAT + Exempt. Figures are locked to this sale and are
            not affected by later VAT rate or apportionment changes.
          </Typography>
        </>
      )}
    </>
  );

  if (bare) return <Box>{body}</Box>;

  return (
    <Card>
      <CardHeader
        title="VAT breakdown"
        subheader="Per product, as charged at the point of sale"
      />
      <CardContent>{body}</CardContent>
    </Card>
  );
}
